import React, { useState, useEffect } from 'react';
import OrgUserService from '../../../services/orgUser.service';
import OrgUserAuthService from '../../../services/orgUserAuth.service';
import { toast } from 'react-toastify';
import '../../../styles/dashboard/OrgUser/OrgUserProfile.css';

export default function OrgUserProfile() {
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await OrgUserService.getProfile();
      setProfile(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error(error.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      const mobileDigits = String(formData.mobileNumber || '').replace(/\D/g, '').slice(-10);
      const normalizedAddress = String(formData.address || '').trim();

      if (mobileDigits) {
        updateData.mobileNumber = mobileDigits;
      }

      if (normalizedAddress) {
        updateData.address = normalizedAddress;
      }

      await OrgUserService.updateProfile(updateData);
      setProfile(formData);
      setEditing(false);
      toast.success('Successfully updated the profile');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await OrgUserAuthService.changePassword(
        passwordData.oldPassword,
        passwordData.newPassword
      );
      setShowPasswordChange(false);
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      toast.success('Password changed successfully');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="orguser-profile">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="orguser-profile">
        <div className="error">Failed to load profile. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="orguser-profile">
      <div className="profile-header">
        <h1>My Profile</h1>
        <p>Manage your account information</p>
      </div>

      <div className="profile-container">
        {/* Profile Information */}
        <div className="profile-section">
          <div className="section-header">
            <h2>Personal Information</h2>
            {!editing && (
              <button
                className="edit-btn"
                onClick={() => setEditing(true)}
              >
                Edit Profile
              </button>
            )}
          </div>

          {editing ? (
            <form onSubmit={handleUpdateProfile} className="profile-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName || ''}
                  disabled
                  className="disabled"
                />
                <small>Full Name cannot be changed</small>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  disabled
                  className="disabled"
                />
                <small>Email cannot be changed</small>
              </div>

              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber || ''}
                  onChange={handleInputChange}
                  pattern="\d{10}"
                  placeholder="10 digit mobile number"
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address || ''}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Aadhaar Number</label>
                  <input
                    type="text"
                    name="aadhaarNumber"
                    value={formData.aadhaarNumber || ''}
                    disabled
                    className="disabled"
                    pattern="\d{12}"
                    placeholder="12 digit Aadhaar"
                  />
                  <small>Aadhaar Number cannot be changed</small>
                </div>

                <div className="form-group">
                  <label>PAN Number</label>
                  <input
                    type="text"
                    name="panNumber"
                    value={formData.panNumber || ''}
                    disabled
                    className="disabled"
                    pattern="[A-Z]{5}[0-9]{4}[A-Z]"
                    placeholder="e.g., ABCDE1234F"
                  />
                  <small>PAN Number cannot be changed</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditing(false);
                    setFormData(profile);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-view">
              <div className="profile-item">
                <label>Full Name</label>
                <p>{profile.fullName}</p>
              </div>

              <div className="profile-item">
                <label>Email</label>
                <p>{profile.email}</p>
              </div>

              <div className="profile-item">
                <label>Username</label>
                <p>{profile.username}</p>
              </div>

              <div className="profile-item">
                <label>Mobile Number</label>
                <p>{profile.mobileNumber || 'Not provided'}</p>
              </div>

              <div className="profile-item">
                <label>Address</label>
                <p>{profile.address || 'Not provided'}</p>
              </div>

              <div className="profile-item">
                <label>Aadhaar Number</label>
                <p>{profile.aadhaarNumber || 'Not provided'}</p>
              </div>

              <div className="profile-item">
                <label>PAN Number</label>
                <p>{profile.panNumber || 'Not provided'}</p>
              </div>

              {/* <div className="profile-item">
                <label>Employee ID</label>
                <p>{profile.employeeIdNumber || 'Not provided'}</p>
              </div> */}

              {profile.organization && (
                <div className="profile-item">
                  <label>Organization</label>
                  <p>{profile.organization.organizationName}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Change Password */}
        <div className="profile-section">
          <div className="section-header">
            <h2>Security</h2>
          </div>

          {!showPasswordChange ? (
            <button
              className="btn btn-primary"
              onClick={() => setShowPasswordChange(true)}
            >
              Change Password
            </button>
          ) : (
            <form onSubmit={handleChangePassword} className="password-form">
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  name="oldPassword"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  minLength="6"
                  required
                />
                <small>Minimum 6 characters</small>
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  minLength="6"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Change Password
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordData({
                      oldPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                    });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Account Information */}
        <div className="profile-section">
          <h2>Account Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Account Created</label>
              <p>
                {profile.createdAt
                  ? new Date(profile.createdAt).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
            <div className="info-item">
              <label>Last Updated</label>
              <p>
                {profile.updatedAt
                  ? new Date(profile.updatedAt).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
            <div className="info-item">
              <label>Account Role</label>
              <p>{profile.role || 'Organization User'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
